import { Card, Box, Group, Stack, Text, ThemeIcon, useComputedColorScheme, type CardProps, type MantineSpacing } from '@mantine/core';
import { type Icon } from '@tabler/icons-react';

export interface SectionCardProps extends Omit<CardProps, 'title' | 'children' | 'p' | 'padding' | 'right'> {
  /** Sarlavha — header'ning chap qismida */
  title: React.ReactNode;
  /** Ixtiyoriy ikonka — sarlavha oldidagi plitka */
  icon?: Icon;
  /** Ikonka plitkasining rangi (mavzu tokenlari) */
  color?: string;
  /** Sarlavha ostidagi ixtiyoriy tavsif */
  subtitle?: React.ReactNode;
  /** Header'ning o'ng tomoni — tugma, filtr, badge */
  right?: React.ReactNode;
  /** Tana qismining ichki bo'shlig'i; jadvalli kartalarda p={0} */
  p?: MantineSpacing;
  /** Sahifa ichidagi havola (anchor) uchun */
  id?: string;
  /** Tana Box'iga qo'shimcha uslub — flex tartibdagi (chat kabi) kartalar uchun */
  bodyStyle?: React.CSSProperties;
  children?: React.ReactNode;
}

/**
 * Sarlavhali (header'li) yagona karta.
 * Header — `Card.Section` bo'lib, tanadan nozik fon va pastki chegara bilan ajraladi.
 * Karta o'zi p={0} bilan chiziladi, tana bo'shlig'ini `p` propi boshqaradi
 * (jadval to'liq kenglikda turishi uchun `p={0}` berish mumkin).
 */
export function SectionCard({
  title,
  icon: IconCmp,
  color = 'brand',
  subtitle,
  right,
  p = 'lg',
  bodyStyle,
  children,
  ...cardProps
}: SectionCardProps) {
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';

  /* `padding={0}`, `p={0}` EMAS. Mantine `--card-padding` ni FAQAT `padding`
     propidan oladi (`Card.cjs` → varsResolver); `p` — oddiy style-prop, u
     o'zgaruvchiga tegmaydi. `p={0}` da `--card-padding` standart `md` (16px)
     bo'lib qolar, `Card.Section:first-child` esa `margin-top: -16px` olardi va
     karta `overflow:hidden` bo'lgani uchun header'ning TEPASI QIRQILARDI —
     sarlavha kartaning chetiga yopishib, tepada bo'shliq yo'qday ko'rinardi. */
  return (
    <Card padding={0} {...cardProps}>
      <Card.Section
        withBorder
        px="lg"
        py="sm"
        // Fon tanadan nozik farq qiladi: yorug'da gray-0, qorong'ida dark-5 (karta dark-6)
        style={{ background: dark ? 'var(--mantine-color-dark-5)' : 'var(--mantine-color-gray-0)' }}
      >
        <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            {IconCmp && (
              <ThemeIcon size={34} radius="md" variant="light" color={color}>
                <IconCmp size={19} stroke={1.8} />
              </ThemeIcon>
            )}
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Text fw={700} fz="sm" lh={1.35} lineClamp={1}>{title}</Text>
              {subtitle && <Text c="dimmed" fz="xs" lh={1.4} lineClamp={1}>{subtitle}</Text>}
            </Stack>
          </Group>
          {right && <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>{right}</Group>}
        </Group>
      </Card.Section>

      <Box p={p} style={bodyStyle}>{children}</Box>
    </Card>
  );
}
